import React, { useState, useEffect, useRef } from 'react';
import { motion } from "framer-motion"

// --- HELPER & UTILITY COMPONENTS (brought in from components.jsx) ---
import Header from "./Header";
import Footer from "./Footer";

import AboutImage from '../../assets/img/About.png';

const useOnScreen = (options) => {
    const ref = useRef();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const currentRef = ref.current; // Capture ref's current value
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsVisible(true);
                if (currentRef) {
                    observer.unobserve(currentRef);
                }
            }
        }, options);

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [ref, options]);

    return [ref, isVisible];
};

const AnimatedSection = ({ children, className, id }) => {
    const [ref, isVisible] = useOnScreen({ threshold: 0.1 });
    return (
        <section ref={ref} id={id} className={`${className} transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {children}
        </section>
    );
};

// const PageHeader = ({ title, subtitle }) => (
//     <section className="relative py-24 bg-slate-50">
//         <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-slate-50 pointer-events-none"></div>
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
//             <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">{title}</h1>
//             <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600">{subtitle}</p>
//         </div>
//     </section>
// );

// --- ICONS FOR THIS PAGE ---
const Target = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></svg>
);

const Users = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
);

const Zap = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
);

const AboutUsPage = () => {
    // --- Added state for header scroll effect ---
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <>
            {/* --- Added Header component here --- */}
            <Header isScrolled={isScrolled} />

            <main>

                {/* Hero Section */}
                <div className="w-full py-20 bg-gradient-to-r from-indigo-100 via-violet-100 to-fuchsia-100">
                    <div className="max-w-7xl mx-auto px-10">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 items-center">

                            <motion.div
                                initial={{ opacity: 0, x: -40 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.7, ease: "easeOut" }}
                                className="text-center md:text-left"
                            >
                                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-fuchsia-800 bg-clip-text text-transparent">
                                    About Outreach AI
                                </h1>

                                <p className="text-lg text-gray-600 max-w-xl mx-auto md:mx-0 mt-6">
                                    AI-driven business outreach designed to help teams connect smarter,
                                    engage better, and grow faster.
                                </p>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, x: 40 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
                                className="flex justify-center"
                            >
                                <img
                                    src={AboutImage}
                                    alt="Outreach AI Platform"
                                    className="w-full max-w-md rounded-2xl shadow-xl"
                                />
                            </motion.div>

                        </div>
                    </div>
                </div>


                {/* Mission Section */}
                <div className="bg-gray-50 py-16">
                    <motion.div
                        initial={{ opacity: 0, x: -40 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-xl sm:text-2xl  md:text-3xl lg:text-4xl  font-bold bg-gradient-to-r from-gray-800 via-blue-700 to-fuchsia-700 bg-clip-text text-transparent mb-4">Our Mission</h2>
                            <p className="text-gray-600 leading-relaxed">
                                Our mission is to empower businesses of all sizes to communicate
                                smarter, not harder. Outreach leverages artificial intelligence to
                                automate repetitive outreach tasks while preserving authenticity
                                and human-like conversation in every interaction.
                            </p>
                        </div>
                        <div className="bg-white shadow-lg rounded-xl p-8 border-x-violet-600 border-[1px] w-full md:w-auto mt-6 md:mt-0">
                            {/* <h3 className="text-xl font-medium mb-3">Why We Exist</h3> */}
                            <p className="text-gray-600">
                                Traditional outreach is broken  generic messages, low response
                                rates, and wasted time. Outreach was built to replace outdated
                                workflows with intelligent, data-driven communication.
                            </p>
                        </div>
                    </motion.div>
                </div>

                {/* What We Do */}
                <div className="py-20">
                    <div className="max-w-6xl mx-auto px-6">
                        <h2 className="text-xl sm:text-2xl  md:text-3xl lg:text-4xl  font-bold bg-gradient-to-r from-gray-800 to-indigo-700 bg-clip-text text-transparent text-center mb-12">
                            What We Do
                        </h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
                            <div className="border rounded-xl p-6 hover:shadow-md transition">
                                <h3 className="text-xl font-semibold mb-2    bg-gradient-to-r from-gray-800 via-blue-700 to-fuchsia-700 bg-clip-text text-transparent animate-pulse ">
                                    AI-Powered Outreach
                                </h3>
                                <p className="text-gray-600">
                                    Outreach automates outreach across email and professional
                                    networks, crafting hyper-personalized messages that feel
                                    natural and relevant.
                                </p>
                            </div>

                            <div className="border rounded-xl p-6 hover:shadow-md transition">
                                <h3 className="text-xl font-semibold mb-2    bg-gradient-to-r from-gray-800 via-blue-700 to-fuchsia-700 bg-clip-text text-transparent animate-pulse">
                                    Intelligent Campaigns
                                </h3>
                                <p className="text-gray-600">
                                    With tools like CampaignCraft and ProspectPath, teams can
                                    target the right audience, optimize messaging, and track
                                    performance in real time.
                                </p>
                            </div>

                            <div className="border rounded-xl p-6 hover:shadow-md transition">
                                <h3 className="text-xl font-semibold mb-2    bg-gradient-to-r from-gray-800 via-blue-700 to-fuchsia-700 bg-clip-text text-transparent animate-pulse">
                                    Virtual AI Assistants
                                </h3>
                                <p className="text-gray-600">
                                    Our AI assistants handle conversations, FAQs, and operational
                                    workflows — reducing workload while improving response quality.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Our Approach */}
                <div className="bg-gray-50 py-20">
                    <div className="max-w-6xl mx-auto px-6">
                        <h2 className="text-xl sm:text-2xl  md:text-3xl lg:text-4xl  font-bold bg-gradient-to-r from-gray-800 to-blue-700 bg-clip-text text-transparent text-center mb-12">
                            Our Approach
                        </h2>

                        <div className="grid md:grid-cols-3 gap-8 text-center">
                            <div className='mx-auto w-56 h-56 sm:w-64 sm:h-64 rounded-full bg-white shadow-md flex flex-col items-center justify-center p-4 sm:p-6'>
                                <h3 className="text-xl font-semibold mb-2">Human-Centered AI</h3>
                                <p className="text-gray-600">
                                    We design AI that enhances human communication instead of
                                    replacing it.
                                </p>
                            </div>

                            <div className='mx-auto w-64 h-64 rounded-full bg-white shadow-md flex flex-col items-center justify-center p-6'>
                                <h3 className="text-xl font-semibold mb-2">Scalable by Design</h3>
                                <p className="text-gray-600">
                                    From startups to enterprises, Outreach scales seamlessly with
                                    your business needs.
                                </p>
                            </div>

                            <div className='mx-auto w-64 h-64 rounded-full bg-white shadow-md flex flex-col items-center justify-center p-6'>
                                <h3 className="text-xl font-semibold mb-2">
                                    Results-Driven
                                </h3>
                                <p className="text-gray-600">
                                    Every feature is built to increase engagement, save time, and
                                    deliver measurable ROI.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Future Vision */}
                <div className="py-20 bg-white">
                    <div className="max-w-4xl mx-auto px-6 text-center relative">
                        <p className="text-lg font-semibold text-gray-700 leading-relaxed">
                            As AI continues to evolve, Outreach remains focused on building smarter
                            automation, deeper insights, and more meaningful business conversations —
                            without sacrificing authenticity.
                        </p>
                    </div>
                </div>



                {/* Contact Section */}
                <div className=" bg-gray-50 py-10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center space-y-1 sm:space-y-2">

                        <h2 className="text-xl sm:text-2xl  md:text-3xl lg:text-4xl  font-bold bg-gradient-to-r from-gray-800 to-blue-700 bg-clip-text text-transparent mb-4 mb-4">Get in Touch</h2>
                        {/* <p className="text-gray-800 mb-2">
                            Holborn Gate, 330 High Holborn, London, WC1V 7QT, United Kingdom
                        </p> */}
                        <p className="text-gray-700">Email: info@hlfppt.org </p>
                    </div>
                </div>

                {/* Our Story Section */}
                {/* <AnimatedSection className="py-24" id="our-story">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid md:grid-cols-2 gap-12 items-center">
                            <div>
                                <h2 className="text-3xl font-extrabold text-slate-900">Our Story</h2>
                                <p className="mt-4 text-lg text-slate-600">
                                    Founded in 2022, Outreach started with a simple idea: outreach shouldn't be a numbers game. It should be about building genuine connections at scale. We saw sales and marketing teams spending countless hours on repetitive tasks, with diminishing returns.
                                </p>
                                <p className="mt-4 text-slate-600">
                                    We knew there was a better way. By harnessing the power of artificial intelligence, we set out to build a platform that automates the tedious work, while amplifying the human touch that truly closes deals. Today, Outreach helps thousands of users connect with their ideal customers in a more meaningful way.
                                </p>
                            </div>
                            <div>
                                <img src="https://placehold.co/600x450/dbeafe/4338ca?text=Our+Journey" alt="A timeline graphic showing growth" className="rounded-2xl shadow-xl" />
                            </div>
                        </div>
                    </div>
                </AnimatedSection> */}

                {/* Our Values Section */}
                {/* <AnimatedSection className="py-24 bg-slate-50" id="our-values">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <h2 className="text-3xl font-extrabold text-slate-900">Our Core Values</h2>
                            <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600">The principles that guide our mission and our work.</p>
                        </div>
                        <div className="mt-16 grid gap-10 md:grid-cols-3">
                            <div className="text-center p-8 bg-white rounded-2xl shadow-lg border">
                                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 text-blue-600 mx-auto">
                                    <Users className="h-8 w-8" />
                                </div>
                                <h3 className="mt-6 text-xl font-bold text-slate-900">Customer-Centric</h3>
                                <p className="mt-2 text-base text-slate-600">We succeed when our customers succeed. We are committed to understanding their needs and delivering exceptional value.</p>
                            </div>
                            <div className="text-center p-8 bg-white rounded-2xl shadow-lg border">
                                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 text-blue-600 mx-auto">
                                    <Zap className="h-8 w-8" />
                                </div>
                                <h3 className="mt-6 text-xl font-bold text-slate-900">Innovate Fearlessly</h3>
                                <p className="mt-2 text-base text-slate-600">We constantly push the boundaries of technology to create powerful, intuitive, and groundbreaking solutions.</p>
                            </div>
                            <div className="text-center p-8 bg-white rounded-2xl shadow-lg border">
                                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 text-blue-600 mx-auto">
                                    <Target className="h-8 w-8" />
                                </div>
                                <h3 className="mt-6 text-xl font-bold text-slate-900">Integrity Above All</h3>
                                <p className="mt-2 text-base text-slate-600">We operate with transparency and honesty in everything we do, building trust with our customers and our team.</p>
                            </div>
                        </div>
                    </div>
                </AnimatedSection> */}

                {/* Our Team Section */}
                {/* <AnimatedSection className="py-24" id="our-team">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <h2 className="text-3xl font-extrabold text-slate-900">Meet the Team</h2>
                            <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600">The passionate minds behind the outreach platform.</p>
                        </div>
                        <div className="mt-16 grid gap-12 sm:grid-cols-2 lg:grid-cols-4">
                            {[
                                { name: "Alex Johnson", role: "CEO & Founder", img: "https://placehold.co/400x400/93c5fd/1e3a8a?text=AJ" },
                                { name: "Brenda Smith", role: "Chief Technology Officer", img: "https://placehold.co/400x400/a7f3d0/052e16?text=BS" },
                                { name: "Chris Lee", role: "Head of Product", img: "https://placehold.co/400x400/f0abfc/4a044e?text=CL" },
                                { name: "Dana White", role: "VP of Sales", img: "https://placehold.co/400x400/fde047/78350f?text=DW" },
                            ].map(member => (
                                <div key={member.name} className="text-center">
                                    <img src={member.img} alt={member.name} className="w-40 h-40 rounded-full mx-auto shadow-lg" />
                                    <h3 className="mt-6 text-xl font-bold text-slate-900">{member.name}</h3>
                                    <p className="text-blue-600 font-semibold">{member.role}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </AnimatedSection> */}
            </main>

            {/* --- Added Footer component here --- */}
            <Footer />
        </>
    );
};

export default AboutUsPage;
